import { IExtendedTransactionParticipants } from 'src/redux/types';
import { Property } from 'src/types';

export const movingOut = (participants: IExtendedTransactionParticipants, property: Property, questionnaire) => {
	const proprietorNames = participants.sellers.reduce<string[]>((acc, seller) => {
		if (seller.relationship !== 'proprietor' || !seller.approved) return acc;

		acc.push(`${seller.givenNames} ${seller.lastNames}`);

		return acc;
	}, []);

	return {
		logoWidth: '100px',
		logoHeight: '100px',
		logoPosition: 'right',
		completedHtml: "<h3>That's it! Thank you for completing the Moving Out card! 🎉</h3>",
		completedBeforeHtml: '<h3>It looks like you have already answered all questions in this section 🧐.</h3>',
		loadingHtml: '<h3>Loading questions...</h3>',
		pages: [
			{
				name: 'timing_financing',
				elements: [
					{
						type: 'boolean',
						name: 'chain_yn',
						title: 'Is this sale tied to the sale or purchase of any other property? *',
						hideNumber: true,
						isRequired: true,
					},
					{
						type: 'paneldynamic',
						name: 'chain',
						visibleIf: '{chain_yn} = true',
						title: 'Related transactions',
						description: 'Please provide details of all related transactions',
						hideNumber: true,
						requiredIf: '{chain_yn} = true',
						templateElements: [
							{
								type: 'tagbox',
								name: 'buyers',
								title: 'Person(s)',
								requiredIf: '{chain_yn} = true',
								choices: proprietorNames,
							},
							{
								type: 'multipletext',
								name: 'property',
								title: 'Property details',
								hideNumber: true,
								requiredIf: '{chain_yn} = true',
								items: [
									{
										name: 'street_address',
										isRequired: true,
										placeholder: 'Flat 12, Churchill Lodge',
										title: 'Street address *',
									},
									{
										name: 'postcode',
										isRequired: true,
										placeholder: 'HP5 1AQ',
										title: 'Postcode *',
									},
									{
										name: 'town_city',
										isRequired: true,
										placeholder: 'Chesham',
										title: 'Town/City *',
									},
									{
										name: 'county_province',
										isRequired: true,
										placeholder: 'Buckinghamshire',
										title: 'County/Province *',
									},
									{
										name: 'state_region',
										isRequired: true,
										placeholder: 'England',
										title: 'State/Region *',
									},
									{
										name: 'country',
										isRequired: true,
										placeholder: 'United Kingdom',
										title: 'Country *',
									},
								],
							},
							{
								type: 'multipletext',
								name: 'buyers_solicitor',
								title: 'Solicitor details',
								hideNumber: true,
								requiredIf: '{chain_yn} = true',
								items: [
									{
										name: 'given_names',
										isRequired: true,
										title: 'Given name(s) *',
									},
									{
										name: 'last_names',
										isRequired: true,
										title: 'Last name(s) *',
									},
									{
										name: 'company',
										isRequired: true,
										title: 'Company *',
									},
									{
										name: 'address',
										title: 'Address',
									},
									{
										name: 'email',
										isRequired: true,
										title: 'Email *',
									},
									{
										name: 'mobile',
										isRequired: true,
										title: 'Mobile *',
									},
								],
							},
							{
								type: 'comment',
								name: 'desc',
								title: 'Tell us more *',
								description: 'e.g. what stage you are at and if there have been any hold-ups',
								hideNumber: true,
								requiredIf: false,
							},
							{
								type: 'boolean',
								name: 'chainbound_yn',
								title: `Does this related transaction require completion on the same day as the sale of ${
									property.address.displayAddress ?? property.name
								}? *`,
								isRequired: true,
							},
							{
								type: 'text',
								name: 'completion_date',
								visible: false,
								visibleIf: '{chain[0].chainbound_yn} = false',
								title: 'What is the expected completion date?',
								description: 'Leave blank if not known yet.',
								titleLocation: 'left',
								hideNumber: true,
								isRequired: false,
								inputType: 'date',
							},
						],
						noEntriesText: ' ',
						panelAddText: 'Add new transaction',
					},
					{
						type: 'text',
						name: 'move_out_date',
						title: 'What is the earliest date you can move out? *',
						hideNumber: true,
						visibleIf: '{chain_yn} = false',
						isRequired: '{chain_yn} = false',
						inputType: 'date',
					},
					{
						type: 'radiogroup',
						name: 'mortgage_sufficient',
						title: 'Will the sale price be sufficient to repay all mortgages and charges secured on the property? *',
						hideNumber: true,
						isRequired: true,
						choices: [
							{
								value: 'yes',
								text: 'Yes',
							},
							{
								value: 'no',
								text: 'No',
							},
							{
								value: 'no_mortgage',
								text: 'No mortgage',
							},
						],
						colCount: 3,
					},
				],
				title: 'Timing and financing',
			},
			{
				name: 'occupiers',
				elements: [
					{
						type: 'boolean',
						name: 'occupiers_seller_yn',
						title: 'Do any of the sellers live at the property? *',
						hideNumber: true,
						isRequired: true,
					},
					{
						type: 'boolean',
						name: 'occupiers_others_yn',
						title: 'Does anyone else, aged 17 or over, live at the property? *',
						description: 'Sellers are excluded',
						hideNumber: true,
						isRequired: true,
					},
					{
						type: 'panel',
						name: 'occupiers_others',
						elements: [
							{
								type: 'paneldynamic',
								name: 'occupiers_others_names',
								visibleIf: '{occupiers_others_yn} = true',
								title: 'Please list all occupiers aged 17 or over *',
								description: 'Sellers are excluded',
								hideNumber: true,
								requiredIf: '{occupiers_others_yn} = true',
								templateElements: [
									{
										type: 'multipletext',
										name: 'occupier',
										visibleIf: '{occupiers_others_yn} = true',
										title: 'Occupier',
										requiredIf: '{occupiers_others_yn} = true',
										items: [
											{
												type: 'text',
												name: 'id',
												title: 'Id *',
												isRequired: true,
												defaultValueExpression: 'randomId()',
											},
											{
												name: 'given_names',
												title: 'Given name(s)',
											},
											{
												name: 'last_names',
												title: 'Last name(s)',
											},
										],
									},
								],
								panelCount: 1,
								minPanelCount: 1,
								panelAddText: 'Add another occupier',
							},
							{
								type: 'boolean',
								name: 'occupiers_tenants_yn',
								visibleIf: '{occupiers_others_yn} = true',
								title: 'Are any of the occupiers who are aged 17 or over, tenants or lodgers? *',
								description: 'Sellers are excluded',
								hideNumber: true,
								requiredIf: '{occupiers_others_yn} = true',
							},
							{
								type: 'boolean',
								name: 'vacant_possession',
								visibleIf: '{occupiers_others_yn} = true',
								title: 'Is the property being sold with vacant possession? *',
								description:
									'“Vacant possession” means the property is empty of people and their belongings (except for anything agreed to stay) on the day of completion.',
								hideNumber: true,
								requiredIf: '{occupiers_others_yn} = true',
							},
							{
								type: 'matrix',
								name: 'vacant_possession_occupiers',
								visible: false,
								visibleIf: '{occupiers_others_yn} = true and {vacant_possession} = true',
								title: 'Have all the occupiers aged 17 or over agreed to: *',
								hideNumber: true,
								requiredIf: '{occupiers_others_yn} = true and {vacant_possession} = true',
								columns: [
									{
										value: 'yes',
										text: 'Yes',
									},
									{
										value: 'no',
										text: 'No',
									},
								],
								rows: [
									{
										value: 'leave',
										text: 'Leave before completion?',
									},
									{
										value: 'sign',
										text: 'Sign the sale contract?',
									},
								],
							},
							{
								type: 'html',
								name: 'vacant_possession_occupiers_note',
								visibleIf:
									"{vacant_possession} = false or  {vacant_possession_occupiers.leave} = 'no' or {vacant_possession_occupiers.sign} = 'no'",
								html: "We'll ask you to upload evidence that the property will be empty on the day of completion in the <b>Documents</b> tab.",
							},
						],
						visibleIf: '{occupiers_others_yn} = true',
						requiredIf: '{occupiers_others_yn} = true',
					},
				],
				title: 'Occupiers',
			},
			{
				name: 'tenants',
				visibleIf: '{occupiers_tenants_yn} = true',
				requiredIf: '{occupiers_tenants_yn} = true',
				title: 'Tenants',
				elements: (questionnaire.occupiers?.occupiers_others_names ?? [])
					.filter((o) => o.occupier?.given_names && o.occupier?.last_names && o.occupier?.id)
					.map((o, i) => ({
						type: 'paneldynamic',
						name: `tenant-${o.occupier.id}`,
						title: `Tenant ${i + 1}: ${o.occupier.given_names} ${o.occupier.last_names}`,
						templateElements: [
							{
								type: 'radiogroup',
								name: `tenancy_type`,
								title: 'What type of tenancy agreement is in place? *',
								isRequired: true,
								choices: [
									{ value: 'Item 1', text: 'Assured Shorthold Tenancy (AST)' },
									{ value: 'Item 2', text: 'Periodic Tenancy' },
								],
								showOtherItem: true,
								hideNumber: true,
							},
							{
								type: 'radiogroup',
								name: `tenancy_term`,
								title: 'Is it a fixed-term contract or a rolling monthly tenancy? *',
								isRequired: true,
								hideNumber: true,
								choices: [
									{ value: 'Item 1', text: 'Fixed-term' },
									{ value: 'Item 2', text: 'Rolling month-to-month' },
								],
							},
							{
								type: 'text',
								name: `notice_period`,
								title: 'What is the notice period? *',
								titleLocation: 'left',
								description: 'In months',
								inputType: 'number',
								isRequired: true,
								hideNumber: true,
							},
							{
								type: 'text',
								name: `monthly_rent`,
								title: 'What is the monthly rent amount? *',
								titleLocation: 'left',
								description: 'In Pounds',
								inputType: 'number',
								isRequired: true,
								hideNumber: true,
							},
							{
								type: 'boolean',
								name: `issues_yn`,
								title: `Have there been any issues with ${o.occupier.given_names} ${o.occupier.last_names} during the tenancy? *`,
								isRequired: true,
								hideNumber: true,
							},
							{
								type: 'comment',
								name: `issues_desc`,
								visibleIf: '{panel.issues_yn} = true',
								title: 'Please provide details *',
								requiredIf: '{panel.issues_yn} = true',
								hideNumber: true,
							},
							{
								type: 'boolean',
								name: `arrears_yn`,
								title: `Is ${o.occupier.given_names} ${o.occupier.last_names} in rent arrears or late on payments? *`,
								isRequired: true,
								hideNumber: true,
							},
							{
								type: 'comment',
								name: `arrears_desc`,
								visibleIf: '{panel.arrears_yn} = true',
								title: 'Please specify the amount and reason *',
								requiredIf: '{panel.arrears_yn} = true',
								hideNumber: true,
							},
							{
								type: 'boolean',
								name: `protected_deposit_yn`,
								title: `Is ${o.occupier.given_names} ${o.occupier.last_names}'s deposit protected in a government-approved scheme? *`,
								isRequired: true,
								hideNumber: true,
							},
						],
					})),
			},
			{
				name: 'hmo',
				title: 'House in Multiple Occupation (HMO)',
				elements: [
					{
						type: 'html',
						name: 'hmo_note',
						html: `
<strong>HMO</strong> stands for <em>“house in multiple occupation”</em> and refers to a residential property where common areas are shared by more than one household, often subdivided from larger homes originally designed for a single family.
<br><br>
Your property is considered an <strong>HMO</strong> if:
<ul style="margin-top: 8px;">
<li>• At least <strong>3 tenants</strong> live in the property, <strong>AND</strong></li>
<li>• The tenants form more than one household (i.e. they are not related or living together as a couple), <strong>AND</strong></li>
<li>• The tenants share toilet, bathroom, or kitchen facilities.</li>
</ul>`,
					},
					{
						type: 'boolean',
						name: 'hmo_yn',
						title: 'Is this property an HMO? *',
						hideNumber: true,
						isRequired: true,
					},
					{
						type: 'boolean',
						name: 'licensed_yn',
						visibleIf: '{hmo_yn} = true',
						title: 'Is the property licensed as an HMO? *',
						requiredIf: '{hmo_yn} = true',
						hideNumber: true,
					},
					{
						type: 'text',
						name: 'license_number',
						visibleIf: '{licensed_yn} = true',
						title: 'What is the license number? *',
						titleLocation: 'left',
						requiredIf: '{licensed_yn} = true',
						hideNumber: true,
					},
					{
						type: 'text',
						name: 'license_expiry_date',
						visibleIf: '{licensed_yn} = true',
						title: 'What is the license expiry date? *',
						titleLocation: 'left',
						requiredIf: '{licensed_yn} = true',
						inputType: 'date',
						hideNumber: true,
					},
					{
						type: 'text',
						name: 'num_shared_facility_users',
						title: 'How many people share the bathroom and kitchen facilities? *',
						titleLocation: 'left',
						inputType: 'number',
						min: 1,
						step: 1,
						hideNumber: true,
						visibleIf: '{hmo_yn} = true',
						requiredIf: '{hmo_yn} = true',
					},
					{
						type: 'boolean',
						name: 'tenant_individual_locks_yn',
						title: 'Are all tenants provided with individual room locks? *',
						hideNumber: true,
						visibleIf: '{hmo_yn} = true',
						requiredIf: '{hmo_yn} = true',
					},
					{
						type: 'boolean',
						name: 'tenant_aware_yn',
						title: 'Are the tenants aware of the property’s HMO status? *',
						hideNumber: true,
						visibleIf: '{hmo_yn} = true',
						requiredIf: '{hmo_yn} = true',
					},
					{
						type: 'boolean',
						name: 'health_and_safety_yn',
						title:
							'Does the property comply with HMO health and safety standards, including fire safety and general maintenance? *',
						hideNumber: true,
						visibleIf: '{hmo_yn} = true',
						requiredIf: '{hmo_yn} = true',
					},
					{
						type: 'comment',
						name: 'health_and_safety_desc',
						visibleIf: '{health_and_safety_yn} = false',
						title: 'Please provide details *',
						requiredIf: '{health_and_safety_yn} = false',
						hideNumber: true,
					},
					{
						type: 'boolean',
						name: 'smoke_and_fire_yn',
						title: 'Is the property equipped with smoke alarms and fire exits in accordance with HMO regulations? *',
						hideNumber: true,
						visibleIf: '{hmo_yn} = true',
						requiredIf: '{hmo_yn} = true',
					},
					{
						type: 'comment',
						name: 'smoke_and_fire_desc',
						visibleIf: '{smoke_and_fire_yn} = false',
						title: 'Please provide details *',
						requiredIf: '{smoke_and_fire_yn} = false',
						hideNumber: true,
					},
					{
						type: 'boolean',
						name: 'enforcement_notices_yn',
						title:
							'Has the property been subject to any enforcement notices from the local authority regarding HMO standards? *',
						hideNumber: true,
						visibleIf: '{hmo_yn} = true',
						requiredIf: '{hmo_yn} = true',
					},
					{
						type: 'comment',
						name: 'enforcement_notices_desc',
						visibleIf: '{enforcement_notices_yn} = true',
						title: 'Please provide details *',
						requiredIf: '{enforcement_notices_yn} = true',
						hideNumber: true,
					},
				],
			},
			{
				name: 'moving_out',
				elements: [
					{
						type: 'boolean',
						name: 'moving_out_clean',
						title:
							'Please confirm that you will remove all rubbish from the property and leave the property in a clean and tidy condition. *',
						hideNumber: true,
						isRequired: true,
					},
					{
						type: 'boolean',
						name: 'moving_out_lights',
						title:
							'Please confirm that, if light fittings are removed, will you replace them with a ceiling rose, flex, bulb holder and blub. *',
						hideNumber: true,
						isRequired: true,
					},
					{
						type: 'boolean',
						name: 'moving_out_care',
						title: 'Please confirm that reasonable care will be taken when removing any other fittings or contents. *',
						hideNumber: true,
						isRequired: true,
					},
					{
						type: 'boolean',
						name: 'moving_out_keys',
						title:
							'Please confirm that keys to all windows and doors and details to alarm codes will be left at the property or with the estate agent. *',
						hideNumber: true,
						isRequired: true,
					},
				],
				title: 'Completion',
			},
		],
		showCompletedPage: false,
		showProgressBar: 'top',
		completeText: 'Submit',
		widthMode: 'static',
	};
};
